import React from 'react';
import { ActivityIndicator, Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { Colors } from '@/constants/theme';
import type { CallFeedback } from '@/services/calls/types';
import { fixAvatarUrl } from '@/utils/avatarUrl';
import { CallFeedbackForm } from './CallFeedbackForm';

/**
 * Presentational shells for the native in-call screen [app/call.tsx]
 * (the web screen [app/call.web.tsx] has its own self-contained call-card UI).
 * WhatsApp-style layout: name + status up top, large avatar in the middle,
 * controls at the bottom.
 */

const AVATAR_SIZE = 150;

/** Large circular callee avatar with an initial fallback. */
export function CallAvatar({ name, image, size = AVATAR_SIZE }: { name: string; image?: string; size?: number }) {
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    const sizeStyle = { width: size, height: size, borderRadius: size / 2 };
    return image ? (
        <Image source={{ uri: fixAvatarUrl(image) }} style={[styles.avatar, sizeStyle]} />
    ) : (
        <View style={[styles.avatar, styles.avatarFallback, sizeStyle]}>
            <Text style={[styles.avatarInitial, { fontSize: size * 0.37 }]}>{initial}</Text>
        </View>
    );
}

interface CallLayoutProps {
    title: string;
    /** The number being dialled; hidden when unknown. */
    phone?: string;
    subtitle: string;
    loading?: boolean;
    body?: React.ReactNode;
    controls?: React.ReactNode;
}

/** Brand background + safe-area layout: header / body / controls. */
export function CallLayout({ title, phone, subtitle, loading, body, controls }: CallLayoutProps) {
    const insets = useSafeAreaInsets();
    return (
        <View style={[styles.screen, { paddingTop: insets.top + 48, paddingBottom: insets.bottom + 32 }]}>
            <View style={styles.header}>
                <Text style={styles.title} numberOfLines={1}>
                    {title}
                </Text>
                {phone ? (
                    <Text style={styles.phone} numberOfLines={1}>
                        {phone}
                    </Text>
                ) : null}
                <View style={styles.subtitleRow}>
                    {loading && <ActivityIndicator size="small" color="#FFF" />}
                    <Text style={styles.subtitle}>{subtitle}</Text>
                </View>
            </View>

            <View style={styles.body}>{body}</View>

            <View style={styles.controls}>{controls}</View>
        </View>
    );
}

export function CallStateView({
    title,
    phone,
    subtitle,
    loading,
    image,
    onCancel,
}: {
    title: string;
    phone?: string;
    subtitle: string;
    loading?: boolean;
    image?: string;
    /** When given, shows a hang-up button so the guide can leave a pending state. */
    onCancel?: () => void;
}) {
    return (
        <CallLayout
            title={title}
            phone={phone}
            subtitle={subtitle}
            loading={loading}
            body={<CallAvatar name={title} image={image} />}
            controls={
                onCancel ? (
                    <TouchableOpacity
                        style={styles.cancelButton}
                        activeOpacity={0.8}
                        onPress={onCancel}
                        accessibilityRole="button"
                        accessibilityLabel="Cancel"
                    >
                        <FontAwesome name="phone" size={28} color="#FFF" />
                    </TouchableOpacity>
                ) : undefined
            }
        />
    );
}

export function CallErrorView({
    message,
    onRetry,
    onClose,
}: {
    message: string;
    onRetry?: () => void;
    onClose: () => void;
}) {
    const insets = useSafeAreaInsets();
    return (
        <View style={[styles.screen, { paddingTop: insets.top + 48, paddingBottom: insets.bottom + 32 }]}>
            <View style={styles.body}>
                <FontAwesome name="exclamation-circle" size={48} color="rgba(255,255,255,0.9)" />
                <Text style={styles.errorText}>{message}</Text>
            </View>
            <View style={styles.errorActions}>
                {onRetry && (
                    <TouchableOpacity style={[styles.actionButton, styles.retryButton]} onPress={onRetry} activeOpacity={0.8}>
                        <Text style={styles.retryText}>Try again</Text>
                    </TouchableOpacity>
                )}
                <TouchableOpacity style={[styles.actionButton, styles.closeButton]} onPress={onClose} activeOpacity={0.8}>
                    <Text style={styles.closeText}>Close</Text>
                </TouchableOpacity>
            </View>
        </View>
    );
}

/**
 * Post-call screen: "Call ended" header over a white card holding the quality
 * survey ([CallFeedbackForm]). Scrolls so the issue list fits small phones.
 * The parent decides what "done" means (send then leave, or just leave).
 */
export function CallFeedbackView({
    title,
    image,
    onSubmit,
    onSkip,
}: {
    title: string;
    image?: string;
    onSubmit: (feedback: CallFeedback) => Promise<void>;
    onSkip: () => void;
}) {
    const insets = useSafeAreaInsets();
    return (
        <ScrollView
            style={styles.screen}
            contentContainerStyle={[styles.feedbackScroll, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 24 }]}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
        >
            <View style={styles.feedbackHeader}>
                <CallAvatar name={title} image={image} size={72} />
                <Text style={styles.title} numberOfLines={1}>
                    {title}
                </Text>
                <Text style={styles.subtitle}>Call ended</Text>
            </View>

            <View style={styles.feedbackCard}>
                <CallFeedbackForm onSubmit={onSubmit} onSkip={onSkip} />
            </View>
        </ScrollView>
    );
}

const styles = StyleSheet.create({
    screen: {
        flex: 1,
        backgroundColor: Colors.brand,
        paddingHorizontal: 24,
    },
    feedbackScroll: {
        flexGrow: 1,
        justifyContent: 'center',
        gap: 20,
    },
    feedbackHeader: {
        alignItems: 'center',
        gap: 8,
    },
    feedbackCard: {
        alignSelf: 'center',
        width: '100%',
        maxWidth: 480,
        backgroundColor: '#fff',
        borderRadius: 16,
        paddingHorizontal: 20,
        paddingTop: 24,
        paddingBottom: 16,
    },
    header: {
        alignItems: 'center',
        gap: 8,
    },
    title: {
        fontSize: 26,
        fontWeight: '700',
        color: '#FFF',
        textAlign: 'center',
    },
    phone: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.85)',
        marginTop: -4,
    },
    subtitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    subtitle: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.85)',
    },
    body: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
    },
    avatar: {
        width: 150,
        height: 150,
        borderRadius: 75,
        backgroundColor: 'rgba(255, 255, 255, 0.18)',
    },
    avatarFallback: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarInitial: {
        fontSize: 56,
        fontWeight: '600',
        color: '#FFF',
    },
    controls: {
        paddingTop: 16,
    },
    cancelButton: {
        alignSelf: 'center',
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: '#E53935',
        justifyContent: 'center',
        alignItems: 'center',
        transform: [{ rotate: '135deg' }],
    },
    errorText: {
        fontSize: 16,
        color: '#FFF',
        textAlign: 'center',
        lineHeight: 22,
    },
    errorActions: {
        gap: 12,
    },
    actionButton: {
        height: 50,
        borderRadius: 25,
        justifyContent: 'center',
        alignItems: 'center',
    },
    retryButton: {
        backgroundColor: '#FFF',
    },
    retryText: {
        color: Colors.brand,
        fontSize: 16,
        fontWeight: '600',
    },
    closeButton: {
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.6)',
    },
    closeText: {
        color: '#FFF',
        fontSize: 16,
        fontWeight: '500',
    },
});
